import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, apiRequest } from '../../api/client.js';
import type { BotCommand, BotSettings } from '../../api/types.js';
import { Alert } from '../../components/Alert.js';
import { PlatformIcon } from '../../components/PlatformIcon.js';
import { StatusPill, type StatusTone } from '../../components/StatusPill.js';
import { Tooltip } from '../../components/Tooltip.js';

/**
 * What the bot is doing, and what is stopping it (§102, module brief §38).
 *
 * ==================== NO FAKE HEALTHY STATE ====================
 * The brief is explicit: "Do not show fake 'healthy' state merely because Bot
 * config is enabled." A platform is `Active` here only when the workspace has
 * switched the bot on for it AND the connection is ACTIVE. Everything else
 * says which of the two is missing, because they have different remedies —
 * one is a toggle on this page, the other is a reconnect on Connections.
 *
 * What this page CANNOT say is whether the provider will accept a send: that
 * is resolved per execution from what was actually granted. The Test Console
 * is where an operator gets that answer, and the link below says so rather
 * than leaving them to guess why an "Active" platform stayed silent.
 * ===============================================================
 */
export function BotOverview({
  settings,
  commands,
  mayManage,
  onChanged,
}: {
  settings: BotSettings;
  commands: BotCommand[];
  mayManage: boolean;
  onChanged: () => Promise<void>;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const patch = async (body: Record<string, unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest('/bot/settings', { method: 'PATCH', body });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const setPlatform = async (platform: string, enabled: boolean): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await apiRequest('/bot/platforms', { method: 'PATCH', body: { platform, enabled } });
      await onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That change could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  const enabledCommands = commands.filter((command) => command.enabled).length;

  return (
    <div className="bot__section">
      {error !== null && <Alert tone="error">{error}</Alert>}

      <div className="bot__row">
        <label className="bot__switch">
          <input
            type="checkbox"
            checked={settings.enabled}
            disabled={!mayManage || busy}
            onChange={(event) => void patch({ enabled: event.target.checked })}
          />
          <span>Connaro Bot is {settings.enabled ? 'on' : 'off'} for this workspace</span>
        </label>

        <div className="composer__field bot__prefix">
          <label className="field__label" htmlFor="bot-prefix">
            Command prefix
          </label>
          <select
            id="bot-prefix"
            className="field__input"
            value={settings.commandPrefix}
            disabled={!mayManage || busy}
            onChange={(event) => void patch({ commandPrefix: event.target.value })}
          >
            {['!', '?', '~', '$', '+', '-'].map((prefix) => (
              <option key={prefix} value={prefix}>
                {prefix}
              </option>
            ))}
          </select>
        </div>
      </div>

      <dl className="bot__stats">
        <div>
          <dt>Commands</dt>
          <dd>
            {String(enabledCommands)} enabled of {String(commands.length)}
          </dd>
        </div>
      </dl>

      <h2 className="bot__subheading">Platforms</h2>

      {settings.platforms.length === 0 ? (
        <p className="field__hint">
          No platform is connected yet. <Link to="/connections">Connect one</Link> before the bot
          has anywhere to speak.
        </p>
      ) : (
        <ul className="bot__platforms">
          {settings.platforms.map((platform) => {
            /*
             * TWO facts, and the label names which one is missing. "Off" and
             * "Reconnect needed" have different remedies in different places,
             * and a single greyed row would send an operator to the wrong one.
             */
            const connected = platform.connectionStatus === 'ACTIVE';
            const state: { tone: StatusTone; label: string; tip: string } = !platform.botEnabled
              ? {
                  tone: 'idle',
                  label: 'Bot off',
                  tip: `Connaro Bot is switched off for ${platform.displayName}. Nothing will be sent there until it is turned on.`,
                }
              : connected
                ? {
                    tone: 'ok',
                    label: 'Active',
                    tip: `The bot is on and the ${platform.displayName} connection is up. Whether ${platform.displayName} accepts a message is resolved each time the bot speaks.`,
                  }
                : {
                    tone: 'warn',
                    label: `Connection ${platform.connectionStatus.toLowerCase().replace(/_/gu, ' ')}`,
                    tip: `The bot is on, but the ${platform.displayName} connection is not healthy. Fix it on the Connections page.`,
                  };

            return (
              <li className="bot__platform" key={platform.platform}>
                <label className="bot__switch">
                  <input
                    type="checkbox"
                    checked={platform.botEnabled}
                    disabled={!mayManage || busy}
                    onChange={(event) =>
                      void setPlatform(platform.platform, event.target.checked)
                    }
                  />
                  {/*
                   * Icon AND name. The mark makes a platform findable at a
                   * glance in a list; the name is what a screen reader reads
                   * and what survives for somebody who does not recognise the
                   * mark. `label` is omitted deliberately — the name is right
                   * beside it, and announcing it twice is worse than not at
                   * all.
                   */}
                  <PlatformIcon
                    platform={platform.platform}
                    displayName={platform.displayName}
                    size={18}
                  />
                  <span className="bot__platform-name">{platform.displayName}</span>
                </label>
                <Tooltip tip={state.tip}>
                  <StatusPill tone={state.tone} label={state.label} />
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}

      {/*
       * SAID OUT LOUD, because it is the question an operator asks next. A
       * platform can read "Active" here and still refuse a send — capability
       * is resolved per execution from what the provider actually granted, and
       * this page is not in a position to know it.
       */}
      <p className="field__hint">
        “Active” means the bot is switched on and the connection is up. Whether a platform will
        accept a message depends on what it granted Connaro, which is resolved each time the bot
        speaks — the Test Console reports it without sending anything.
      </p>
    </div>
  );
}
